import PickupRoutes from 'pages/pickup/routes';
import React, { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { message } from 'antd';
import LoginPage from './pages/LoginPage';
import TutorialVideoPage from './pages/delivery/splitOrder/components/tutorialVideo/TutorialVideoPage';
import CentralRoutes from './pages/central/routes';
import DeliveryRoutes from './pages/delivery/routes';
import PartnerControlRoutes from './pages/partner-control/routes';
import DriverOpsRoutes from './pages/driver-ops/routes';
import WmsRoutes from './pages/wms/routes';
import TmsRoutes from './pages/tms/routes';
import QualityControlRoutes from './pages/qualitycontrol/routes';
import ConfigRoutes from './pages/config/routes';
import MapToolRoutes from './pages/map-tool/routes';
import OrderCloseRoutes from './pages/order-close/routes';
import DashboardRoutes from './pages/dashboard/routes';
import SettlementRoutes from './pages/central/settlement/routes';
import OpsToolRoutes from './pages/ops-tool/routes';
import DataPortalRoutes from './pages/data-portal/routes';
import ComplaintPlatformRoutes from './pages/complaint-platform/routes';
import { AuthGuard } from './components/AuthGuard';
import MainLayout from "./layouts/MainLayout";
import { useFetchResourceCodes } from './hooks/useFetchResourceCodes';
import { useFetchWarehouseIds } from './hooks/useFetchWarehouseIds';
import { useFetchDataPortal } from './hooks/useFetchDataPortal';
import { useFetchWatermark } from './hooks/useFetchWatermark';
import Watermark from './components/Watermark';
import { translateGraphQLError } from './utils/graphqlUtils';

const PERMISSION_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

const App: React.FC = () => {
    const { fetchResourceCodes } = useFetchResourceCodes();
    const { fetchWarehouseIds } = useFetchWarehouseIds();
    const { fetchDataPortal } = useFetchDataPortal();
    const { fetchWatermark } = useFetchWatermark();
    const [ready, setReady] = useState(false);

    useEffect(() => {
        fetchResourceCodes()
            .catch((e: any) => {
                console.error('Resource codes fetch on page load failed', e);
                void message.error(translateGraphQLError(e, 'errors.auth.fetchResourceCodesFailed'));
            })
            .finally(() => setReady(true));

        // 数据门户菜单信号：拉不到不影响其它菜单，静默降级为「不展示数据门户」。
        // 不需要等它才渲染——结果进 store 后会自动触发一级菜单重算（见 store/dataPortalStore）
        void fetchDataPortal()
            .catch((e: any) => { console.warn('Data portal fetch on page load failed', e); });

        // 页面水印：拉不到就降级为不显示，不弹提示、不阻塞业务（plan 决策 5）。
        // 同样不需要 await——结果进 store 后覆盖层会自动挂上（见 store/watermarkStore）
        void fetchWatermark()
            .catch((e: any) => { console.warn('Watermark fetch on page load failed', e); });

        void fetchWarehouseIds()
            .catch((e: any) => {
                console.error('Warehouse ids fetch on page load failed', e);
                void message.error(translateGraphQLError(e, 'errors.auth.getWarehouseIdsFailed'));
            });

        const intervalId = setInterval(() => {
            fetchResourceCodes()
                .catch((e) => { console.warn('Resource codes periodic fetch failed', e); });
            fetchWarehouseIds()
                .catch((e) => { console.warn('Warehouse ids periodic fetch failed', e); });
            fetchDataPortal()
                .catch((e) => { console.warn('Data portal periodic fetch failed', e); });
            // 顺带满足水印规范的「跨零点长时间挂机的页面需在下一次数据刷新时同步更新日期」
            fetchWatermark()
                .catch((e) => { console.warn('Watermark periodic fetch failed', e); });
        }, PERMISSION_REFRESH_INTERVAL_MS);

        return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!ready) return null;

    return (
        <div className="app-container">
            {/* 全站水印覆盖层：挂在应用根，才能盖住 portal 到 body 的 Modal/Drawer
                与渲染在模块 Layout 之外的全屏页；未登录时组件自身不渲染 */}
            <Watermark />
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/tutorial-video" element={<AuthGuard><TutorialVideoPage /></AuthGuard>} />
                <Route path="/" element={<AuthGuard><MainLayout /></AuthGuard>} />
                <Route path="/delivery/*" element={<DeliveryRoutes />} />
                <Route path="/partner-control/*" element={<PartnerControlRoutes />} />
                <Route path="/driver-ops/*" element={<DriverOpsRoutes />} />
                <Route path="/central/*" element={<CentralRoutes />} />
                <Route path="/wms/*" element={<WmsRoutes />} />
                <Route path="/pickup/*" element={<PickupRoutes />} />
                <Route path="/tms/*" element={<TmsRoutes />} />
                <Route path="/qualitycontrol/*" element={<QualityControlRoutes/>}/>
                <Route path="/config/*" element={<ConfigRoutes />} />
                <Route path="/map-tool/*" element={<MapToolRoutes/>}/>
                <Route path="/order-close/*" element={<OrderCloseRoutes />} />
                <Route path="/dashboard/*" element={<DashboardRoutes />} />
                <Route path="/settlement/*" element={<SettlementRoutes />} />
                <Route path="/ops-tool/*" element={<OpsToolRoutes />} />
                <Route path="/data-portal/*" element={<DataPortalRoutes />} />
                <Route path="/complaint-platform/*" element={<ComplaintPlatformRoutes />} />
            </Routes>
        </div>
    );
};

export default App;